import { Font, StyleSheet } from "@react-pdf/renderer";

// TODO: I believe there is a way to grab this dynamically, but can't find. Apparently react-pdf can only use static fonts, might want a cdn hosted version for us
Font.register({
    family: "Inter",
    fonts: [
        { src: "/fonts/Inter-Regular.ttf", fontWeight: 400 }, // font-style: normal, font-weight: normal
        { src: "/fonts/Inter-Italic.ttf", fontStyle: "italic", fontWeight: 400 },
        { src: "/fonts/Inter-Medium.ttf", fontWeight: 500 },
        { src: "/fonts/Inter-MediumItalic.ttf", fontStyle: "italic", fontWeight: 500 },
        { src: "/fonts/Inter-Bold.ttf", fontWeight: 700 },
        { src: "/fonts/Inter-BoldItalic.ttf", fontStyle: "italic", fontWeight: 700 },
        { src: "/fonts/Inter-SemiBold.ttf", fontWeight: 600 },
        { src: "/fonts/Inter-SemiBoldItalic.ttf", fontStyle: "italic", fontWeight: 600 },
    ],
});

Font.register({
    family: "RobotoMono",
    fonts: [{ src: "/fonts/RobotoMono-Medium.ttf", fontWeight: 500 }],
});

// https://react-pdf.org/advanced#hyphenation
Font.registerHyphenationCallback((word) => [word]);

export const pdfColors = {
    Black: "#000000",
    Gray80: "#333333",
    Gray75: "#404040",
    Gray65: "#595959",
    Gray50: "#808080",
    Gray20: "#cccccc",
    Gray10: "#e5e5e5",
    Gray2: "#fafafa",
    White: "#ffffff",
    LobbieBlue: "#0026ca",
    LobbieLightBlue: "#d2e6ff",
};

const pdfWeights = {
    Bold: 700,
    Semi: 600,
    Med: 500,
    Reg: 400,
};

const pdfSizes = {
    H0: 18,
    H1: 14,
    H2: 12,
    H3: 10,
    H4: 8,
    Ls: 10,
    Ms: 8,
    Ss: 6,
};

const pdfSides = {
    t: "Top",
    b: "Bottom",
    r: "Right",
    l: "Left",
};

type TextColors = {
    [Property in keyof typeof pdfColors as `text${string & Property}`]: { color: string };
};
const textColors = {
    ...Object.fromEntries(
        Object.entries(pdfColors).map(([name, color]) => [`text${name}`, { color }]),
    ),
} as TextColors;

type TextWeights = {
    [Property in keyof typeof pdfWeights as `text${string & Property}`]: { fontWeight: number };
};
const textWeights = {
    ...Object.fromEntries(
        Object.entries(pdfWeights).map(([name, weight]) => [`text${name}`, { fontWeight: weight }]),
    ),
} as TextWeights;

type TextSizes = {
    [Property in keyof typeof pdfSizes as `text${string & Property}`]: { fontSize: number };
};
const textSizes = {
    ...Object.fromEntries(
        Object.entries(pdfSizes).map(([name, size]) => [`text${name}`, { fontSize: size }]),
    ),
} as TextSizes;

const pdfSpacing = [32, 24, 20, 16, 12, 10, 8, 6, 4, 2];
type SpacingSizesDef = 32 | 24 | 20 | 16 | 12 | 10 | 8 | 6 | 4 | 2;
const pdfBorderSize = [1, 2, 3, 4];
type BorderSizesDef = 1 | 2 | 3 | 4;

// All the type defs for Padding and Margins
// TODO: The "industry" at large has discovered that long term: reusable styled components > reusable css
type PKeyDefs = `p${SpacingSizesDef}`;
type PStyleDef = { [key in PKeyDefs]: { padding: number } };
type PTKeyDefs = `pt${SpacingSizesDef}`;
type PTStyleDef = { [key in PTKeyDefs]: { paddingTop: number } };
type PBKeyDefs = `pb${SpacingSizesDef}`;
type PBStyleDef = { [key in PBKeyDefs]: { paddingBottom: number } };
type PLKeyDefs = `pl${SpacingSizesDef}`;
type PLStyleDef = { [key in PLKeyDefs]: { paddingLeft: number } };
type PRKeyDefs = `pr${SpacingSizesDef}`;
type PRStyleDef = { [key in PRKeyDefs]: { paddingRight: number } };
type PaddingStyles = PStyleDef & PTStyleDef & PBStyleDef & PLStyleDef & PRStyleDef;

type MKeyDefs = `m${SpacingSizesDef}`;
type MStyleDef = { [key in MKeyDefs]: { margin: number } };
type MTKeyDefs = `mt${SpacingSizesDef}`;
type MTStyleDef = { [key in MTKeyDefs]: { marginTop: number } };
type MBKeyDefs = `mb${SpacingSizesDef}`;
type MBStyleDef = { [key in MBKeyDefs]: { marginBottom: number } };
type MLKeyDefs = `ml${SpacingSizesDef}`;
type MLStyleDef = { [key in MLKeyDefs]: { marginLeft: number } };
type MRKeyDefs = `mr${SpacingSizesDef}`;
type MRStyleDef = { [key in MRKeyDefs]: { marginRight: number } };
type MarginStyles = MStyleDef & MTStyleDef & MBStyleDef & MLStyleDef & MRStyleDef;

type PaddingAndMarginStyles = PaddingStyles & MarginStyles;
const paddingAndMarginStyles = {
    ...Object.fromEntries(
        pdfSpacing.flatMap((size) => [
            [`p${size}`, { padding: size }],
            [`m${size}`, { margin: size }],
            ...Object.entries(pdfSides).flatMap(([initial, side]) => [
                [`p${initial}${size}`, { [`padding${side}`]: size }],
                [`m${initial}${size}`, { [`margin${side}`]: size }],
            ]),
        ]),
    ),
} as PaddingAndMarginStyles;

// All the type defs for Borders
// TODO: The "industry" at large has discovered that long term: reusable styled components > reusable css
type BorderSizeColorKeyDef = `border${BorderSizesDef}${keyof typeof pdfColors}`;
type BorderSizeColorStyleDef = {
    [key in BorderSizeColorKeyDef]: { borderWidth: number; borderColor: string };
};
type BorderSizeColorTopKeyDef = `border${BorderSizesDef}${keyof typeof pdfColors}T`;
type BorderSizeColorTopStyleDef = {
    [key in BorderSizeColorTopKeyDef]: { borderTopWidth: number; borderTopColor: string };
};
type BorderSizeColorBottomKeyDef = `border${BorderSizesDef}${keyof typeof pdfColors}B`;
type BorderSizeColorBottomStyleDef = {
    [key in BorderSizeColorBottomKeyDef]: { borderBottomWidth: number; borderBottomColor: string };
};
type BorderSizeColorLeftKeyDef = `border${BorderSizesDef}${keyof typeof pdfColors}L`;
type BorderSizeColorLeftStyleDef = {
    [key in BorderSizeColorLeftKeyDef]: { borderLeftWidth: number; borderLeftColor: string };
};
type BorderSizeColorRightKeyDef = `border${BorderSizesDef}${keyof typeof pdfColors}R`;
type BorderSizeColorRightStyleDef = {
    [key in BorderSizeColorRightKeyDef]: { borderRightWidth: number; borderRightColor: string };
};
type BorderStyles = BorderSizeColorStyleDef &
    BorderSizeColorTopStyleDef &
    BorderSizeColorBottomStyleDef &
    BorderSizeColorLeftStyleDef &
    BorderSizeColorRightStyleDef;
const borderStyles = {
    ...Object.fromEntries(
        pdfBorderSize.flatMap((size) =>
            Object.entries(pdfColors).flatMap(([colorName, colorHex]) => [
                [`border${size}${colorName}`, { borderWidth: size, borderColor: colorHex }],
                ...Object.entries(pdfSides).flatMap(([initial, side]) => [
                    [
                        `border${size}${colorName}${initial.toUpperCase()}`,
                        { [`border${side}Width`]: size, [`border${side}Color`]: colorHex },
                    ],
                ]),
            ]),
        ),
    ),
} as BorderStyles;

const styles = StyleSheet.create({
    page: {
        padding: 20,
        fontFamily: "Inter",
    },
    //Tale up available space to push footer to bottom, always (I Hope)
    flex1: {
        flex: 1,
    },
    colTitle: {
        width: "30%",
        justifyContent: "center",
    },
    colContent: {
        width: "70%",
        justifyContent: "center",
    },
    center: {
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
    },
    footer: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    row: {
        flexDirection: "row",
    },
    col: {
        flexDirection: "column",
    },
    w100: {
        width: "100%",
    },
    // Draft and final styles
    dBadge: {
        borderRadius: 2,
        color: "#002c6a",
        backgroundColor: "#d2e6ff",
    },
    fBadge: {
        borderRadius: 2,
        color: "#034425",
        backgroundColor: "#c2f9c4",
    },
    ksBadgeUnknown: {
        borderRadius: 2,
        borderWidth: 1,
        borderColor: "#333333",
        color: "#333333",
        backgroundColor: "#ffffff",
    },
    ksBadgeNoKnown: {
        borderRadius: 2,
        color: "#ffffff",
        backgroundColor: "#28A31D",
    },
    ksBadgeReported: {
        borderRadius: 2,
        color: "#900303",
        backgroundColor: "#F7D8D8",
    },
    justBetween: {
        justifyContent: "space-between",
    },
    logo: {
        width: 100,
    },
    table: {
        display: "flex",
        width: "auto",
        borderTopStyle: "solid",
        borderTopWidth: "0.5px",
        borderLeftWidth: "0.5px",
        borderLeftStyle: "solid",
        borderColor: "#CCCCCC",
    },
    tableRow: {
        flexDirection: "row",
        alignItems: "stretch",
        borderBottomStyle: "solid",
        borderBottomWidth: "0.5px",
        borderBottomColor: "#CCCCCC",
    },
    cell: {
        flexDirection: "column",
        padding: "4px 6px 4px 6px",
        flex: 1,
        justifyContent: "center",
        // minWidth: "90.4px",
        gap: "2px",
        borderRight: "solid",
        borderRightWidth: "0.5px",
        borderRightColor: "#CCCCCC",
    },
    headerCell: {
        fontWeight: "bold",
        backgroundColor: "#FAFAFA",
    },
    nameCell: {
        minWidth: "120px",
    },
    textMono: {
        fontFamily: "RobotoMono",
        fontWeight: 500,
    },

    commentContainer: {
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 4,
        paddingBottom: 4,
        marginBottom: 4,
        backgroundColor: "#f5f5f5",
        borderTop: "none",
        borderBottom: "1px solid #e0e0e0",
    },

    bgLightGray: {
        backgroundColor: "#f5f5f5",
    },
    commentText: {
        fontSize: 9,
        color: "#333333",
    },

    // Text colors, e.g. textBlack or textGray80 (see colors above)
    ...textColors,

    // Text weights, textBold, textSbold, textReg
    ...textWeights,

    // Text sizes, textH0, textH1, TextSm
    ...textSizes,

    // Padding and margin, pp32, pr32, pm8, pmt24
    ...paddingAndMarginStyles,

    // Border styles, border2Gray20 (border all around, 2 width, Gray20 color), border1Gray80T (border, 1 width, Gray80, Top only)
    ...borderStyles,
});

export default styles;
